import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import ToolResultCard from "@/components/tools/ToolResultCard"
import { NumberField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, NumberedSteps, FeatureGrid, FormulaBox, ExampleBox, TierCards, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-engagement-calculator")!

export const Route = createFileRoute("/tools/instagram-engagement-calculator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-engagement-calculator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-engagement-calculator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-engagement-calculator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-engagement-calculator" },
      ]}),
      createHowToSchema({ name: `How to Calculate Instagram Engagement Rate`, description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: EngagementCalculatorPage,
})

function EngagementCalculatorPage() {
  const [followers, setFollowers] = useState("")
  const [posts, setPosts] = useState("")
  const [likes, setLikes] = useState("")
  const [comments, setComments] = useState("")
  const [result, setResult] = useState<{
    rate: number
    avgLikes: number
    avgComments: number
    totalEngagement: number
    rating: string
    variant: "success" | "primary" | "warning" | "danger"
  } | null>(null)

  const calculate = () => {
    const f = Number(followers)
    const p = Number(posts)
    const l = Number(likes)
    const c = Number(comments)
    if (!f || !p || f <= 0 || p <= 0) return

    const rate = ((l + c) / (f * p)) * 100
    const avgLikes = l / p
    const avgComments = c / p

    let rating: string
    let variant: "success" | "primary" | "warning" | "danger"
    if (rate > 6) { rating = "Excellent"; variant = "success" }
    else if (rate >= 3) { rating = "Good"; variant = "primary" }
    else if (rate >= 1) { rating = "Average"; variant = "warning" }
    else { rating = "Low"; variant = "danger" }

    setResult({ rate, avgLikes, avgComments, totalEngagement: l + c, rating, variant })
  }

  const reset = () => {
    setFollowers("")
    setPosts("")
    setLikes("")
    setComments("")
    setResult(null)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          {/* Section 1: What is Instagram Engagement Rate? */}
          <ContentSection>
            <SectionHeading>What is Instagram Engagement Rate?</SectionHeading>
            <Paragraph>
              Instagram engagement rate measures how actively your audience interacts with your content relative to your follower count. It is widely considered the single most important metric for creators, influencers, and brands on the platform — more valuable than follower count, impressions, or even reach. Why? Because engagement rate reveals the <strong>real quality</strong> of your audience, not just its size.
            </Paragraph>
            <Paragraph className="mt-3">
              When someone likes, comments on, saves, or shares your post, they are telling Instagram's algorithm that your content is worth seeing. The higher your engagement rate, the more the algorithm amplifies your content to new audiences through the Explore page, Reels feed, and suggested posts. A creator with 5,000 highly engaged followers will consistently outperform an account with 50,000 ghost followers that never interact.
            </Paragraph>
            <Paragraph className="mt-3">
              Engagement rate also serves as the universal currency of influencer marketing. When brands evaluate creators for sponsorships, they look at engagement rate first because it predicts how many people will actually see and act on a sponsored post. An account with a 5% engagement rate on 10K followers delivers more value than an account with 0.5% on 100K. Understanding and tracking this metric is the foundation of every serious Instagram growth strategy.
            </Paragraph>
          </ContentSection>

          {/* Section 2: How to Calculate Instagram Engagement Rate */}
          <ContentSection>
            <SectionHeading>How to Calculate Instagram Engagement Rate</SectionHeading>
            <Paragraph>
              Calculating your engagement rate is straightforward once you know where to find the numbers. Follow these three steps to get an accurate measurement of your account performance.
            </Paragraph>

            <NumberedSteps steps={[
              {
                title: "Count Your Followers",
                description: "Open your Instagram profile and note your current follower count. This is the denominator in the engagement formula and represents your potential audience size. If your follower count fluctuates significantly (gaining or losing hundreds per day), use the count from the midpoint of the period you are analyzing for the most accurate result.",
              },
              {
                title: "Add Up Your Engagement",
                description: "Look at your most recent 10 posts (or however many you want to analyze). For each post, record the number of likes and comments. Add all the likes together, then add all the comments together. Using 10 posts gives you a reliable average that smooths out outliers — one viral post or one flop will not skew your overall rate.",
              },
              {
                title: "Use the Formula",
                description: "Plug your numbers into the engagement rate formula: divide total engagement (likes + comments) by (followers multiplied by the number of posts), then multiply by 100 to get a percentage. Here is a worked example:",
              },
            ]} />

            <ExampleBox title="Worked Example">
              <p>
                You have <strong>8,000 followers</strong>. Your last <strong>10 posts</strong> received a total of <strong>1,200 likes</strong> and <strong>180 comments</strong>.<br />
                Total Engagement = 1,200 + 180 = <strong>1,380</strong><br />
                Engagement Rate = (1,380 / (8,000 x 10)) x 100 = (1,380 / 80,000) x 100 = <strong>1.73%</strong>
              </p>
            </ExampleBox>

            <Paragraph className="mt-3">
              That 1.73% falls in the average range. Keep reading to see exactly what your number means and how to push it higher.
            </Paragraph>
          </ContentSection>

          {/* Section 3: Engagement Rate Formula */}
          <ContentSection>
            <SectionHeading>Engagement Rate Formula</SectionHeading>
            <FormulaBox>
              Engagement Rate = ((Total Likes + Total Comments) / (Followers x Number of Posts)) x 100
            </FormulaBox>
            <Paragraph className="mt-4">
              Let's break down each variable:
            </Paragraph>
            <FeatureGrid columns={2} items={[
              { title: "Total Likes", description: "The sum of all likes across the posts you are analyzing." },
              { title: "Total Comments", description: "The sum of all comments across those same posts (excluding your own replies)." },
              { title: "Followers", description: "Your current follower count at the time of analysis." },
              { title: "Number of Posts", description: "How many posts you included in the analysis (typically 10-30 for a reliable average)." },
            ]} />

            <Paragraph className="mt-5 font-semibold">Examples at different follower counts:</Paragraph>
            <ExampleBox title="Nano creator (2,500 followers)">
              <p>
                10 posts, 800 likes, 120 comments<br />
                ((800 + 120) / (2,500 x 10)) x 100 = <strong>3.68%</strong> — Good
              </p>
            </ExampleBox>
            <ExampleBox title="Mid-tier creator (25,000 followers)">
              <p>
                10 posts, 3,500 likes, 400 comments<br />
                ((3,500 + 400) / (25,000 x 10)) x 100 = <strong>1.56%</strong> — Average
              </p>
            </ExampleBox>
            <ExampleBox title="Large creator (150,000 followers)">
              <p>
                10 posts, 18,000 likes, 2,200 comments<br />
                ((18,000 + 2,200) / (150,000 x 10)) x 100 = <strong>1.35%</strong> — Average (typical for larger accounts)
              </p>
            </ExampleBox>
            <Paragraph className="mt-3">
              Notice that engagement rate naturally decreases as follower count grows. A 1.5% rate on 150K followers is perfectly normal, while the same rate on 2K followers would signal room for improvement.
            </Paragraph>
          </ContentSection>

          {/* Section 4: What Your Engagement Rate Means */}
          <ContentSection>
            <SectionHeading>What Your Engagement Rate Means</SectionHeading>
            <Paragraph className="mb-4">
              Not all engagement rates are equal. Here is a breakdown of the benchmark tiers so you know exactly where you stand and what to aim for.
            </Paragraph>

            <TierCards tiers={[
              {
                label: "Excellent",
                range: "Above 6%",
                description: "You are in the top tier of Instagram creators. Your audience is deeply connected to your content and actively engages with nearly every post. At this level, brands consider you a high-value partner regardless of follower count. This rate is most common among nano and micro-influencers (under 10K followers) who have built tight-knit communities. If you are above 6% with more than 50K followers, you are doing something exceptional — keep doing it.",
                color: "green",
              },
              {
                label: "Good",
                range: "3% - 6%",
                description: "You have a healthy, active audience. Your content resonates well and the algorithm is likely rewarding you with decent reach. Most successful creators with 10K-50K followers land in this range. Brands are comfortable partnering at this level, and you have a strong foundation to scale. Focus on consistency and experimenting with new formats (Reels, carousels) to push toward the excellent tier.",
                color: "blue",
              },
              {
                label: "Average",
                range: "1% - 3%",
                description: "This is the most common range on Instagram, especially for accounts with 10K+ followers. It is not bad, but it does signal that a chunk of your audience is passive — they follow you but rarely interact. There is significant upside here. Small changes like stronger calls-to-action, posting at peak hours, and replying to every comment can push you into the good range within weeks.",
                color: "yellow",
              },
              {
                label: "Low",
                range: "Under 1%",
                description: "A sub-1% engagement rate is a red flag. It often indicates a high proportion of inactive, bot, or purchased followers. The algorithm notices low engagement and will throttle your reach, creating a downward spiral. The fix: audit your follower list and remove fake accounts, revisit your content strategy, and focus on creating genuinely valuable posts for your target audience. Sometimes it is better to have fewer real followers than many ghost ones.",
                color: "red",
              },
            ]} />
          </ContentSection>

          {/* Section 5: Why Track Instagram Engagement Rate? */}
          <ContentSection>
            <SectionHeading>Why Track Instagram Engagement Rate?</SectionHeading>
            <Paragraph className="mb-4">
              Tracking your engagement rate is not vanity — it is strategy. Here are the four biggest reasons every creator and brand should monitor this metric consistently.
            </Paragraph>

            <FeatureGrid columns={2} items={[
              {
                icon: "📈",
                title: "Algorithm Boost",
                description: "Instagram's algorithm uses engagement as the primary signal to decide which content gets distributed. When your posts receive likes, comments, and saves within the first 30-60 minutes of publishing, Instagram interprets that as high-quality content and pushes it to the Explore page, Reels feed, and suggested posts. A consistently strong engagement rate essentially trains the algorithm to give you free reach — the most valuable asset on the platform.",
              },
              {
                icon: "🤝",
                title: "Brand Deals and Sponsorships",
                description: "The influencer marketing industry has matured past the point of paying for followers alone. Brands and agencies now use engagement rate as the primary filter when selecting creators for campaigns. A media kit that shows a 4.5% engagement rate on 15K followers will win deals over a 0.8% rate on 100K followers. Tracking your rate lets you confidently pitch to brands with real data that proves your audience takes action.",
              },
              {
                icon: "🎯",
                title: "Content Strategy Optimization",
                description: "When you calculate engagement rate regularly, you start seeing patterns. Maybe carousels consistently outperform single images. Maybe Reels about tips get twice the comments of behind-the-scenes content. This data removes the guesswork from your content calendar and lets you double down on what actually resonates with your audience. Over time, the creators who track and iterate always outperform those who post blindly.",
              },
              {
                icon: "💚",
                title: "Audience Health Check",
                description: "A sudden drop in engagement rate is one of the earliest warning signs that something is off. It could mean you gained a batch of bot followers from a viral post, your content direction shifted away from what your audience wants, or Instagram changed its algorithm. By monitoring engagement weekly, you catch these issues early and can course-correct before your reach takes a major hit. Think of engagement rate as the vital sign of your Instagram account — check it regularly to stay healthy.",
              },
            ]} />
          </ContentSection>

          {/* Section 6: How to Improve Your Engagement Rate */}
          <ContentSection>
            <SectionHeading>How to Improve Your Engagement Rate</SectionHeading>
            <Paragraph className="mb-4">
              Knowing your engagement rate is step one. Improving it is where the real growth happens. Here are eight proven tactics that work in 2026.
            </Paragraph>

            <TipList tips={[
              {
                title: "Post consistently",
                description: "Aim for 3-5 feed posts per week. The algorithm rewards consistency, and your audience needs regular reasons to engage. A predictable schedule builds anticipation and habit.",
              },
              {
                title: "Reply to comments within 1 hour",
                description: "Early replies boost the comment count and signal to the algorithm that your post is sparking conversation. It also encourages followers to comment again knowing you will respond.",
              },
              {
                title: "Use carousel posts",
                description: "Carousels have the highest save rates of any post type on Instagram. They keep users swiping, which increases time spent on your content — a key algorithm signal. Educational carousels and step-by-step tutorials perform especially well.",
              },
              {
                title: "Post Reels regularly",
                description: "Reels get 2-3x more reach than static posts because Instagram actively promotes short-form video. Even repurposing existing content into a 15-second Reel can dramatically expand your audience.",
              },
              {
                title: "Ask questions in your captions",
                description: "A simple question at the end of your caption can double your comment count. Make it easy to answer — \"Which one would you pick, A or B?\" works better than open-ended questions.",
              },
              {
                title: "Use relevant hashtags (3-5 per post)",
                description: "Hashtags are still one of the best free discovery tools on Instagram. Use a mix of niche-specific and mid-volume hashtags rather than ultra-popular ones where your content gets buried instantly.",
              },
              {
                title: "Engage with niche communities",
                description: "Spend 15-20 minutes before and after posting to like and comment on posts from accounts in your niche. This puts your profile in front of new potential followers who are already interested in your type of content.",
              },
              {
                title: "Use DM automation to capture engagement",
                description: "Tools like Zapify let you automatically respond to comments with personalized DMs, turning passive interactions into conversations. This boosts both your comment engagement and DM engagement simultaneously — and it works 24/7 while you sleep.",
              },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        {/* Calculator Card */}
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!result ? (
            <div className="space-y-4">
              <NumberField label="Total Followers" value={followers} onChange={setFollowers} placeholder="e.g. 5000" required min={1} />
              <NumberField label="Number of Posts to Analyze" value={posts} onChange={setPosts} placeholder="e.g. 10" required min={1} max={100} />
              <div className="grid grid-cols-2 gap-4">
                <NumberField label="Total Likes" value={likes} onChange={setLikes} placeholder="e.g. 450" required min={0} />
                <NumberField label="Total Comments" value={comments} onChange={setComments} placeholder="e.g. 50" required min={0} />
              </div>
              <SubmitButton onClick={calculate} disabled={!followers || !posts}>
                Calculate Engagement Rate
              </SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Your Engagement Rate</p>
                <p className={`text-5xl sm:text-6xl font-bold ${
                  result.variant === "success" ? "text-green-600 dark:text-green-400" :
                  result.variant === "primary" ? "text-brand-button" :
                  result.variant === "warning" ? "text-yellow-600 dark:text-yellow-400" :
                  "text-red-600 dark:text-red-400"
                }`}>
                  {result.rate.toFixed(2)}%
                </p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold ${
                  result.variant === "success" ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400" :
                  result.variant === "primary" ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400" :
                  result.variant === "warning" ? "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400" :
                  "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
                }`}>
                  {result.rating}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <ToolResultCard label="Avg Likes/Post" value={result.avgLikes.toFixed(1)} />
                <ToolResultCard label="Avg Comments/Post" value={result.avgComments.toFixed(1)} />
                <ToolResultCard label="Total Engagement" value={result.totalEngagement.toLocaleString()} />
              </div>
              <ResetButton onClick={reset} />
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
